import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
          headers: {
            Authorization: `Bearer ${refreshToken}`,
          },
        });

        const { access_token } = response.data;
        localStorage.setItem('access_token', access_token);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
  forgotPassword: (data) => api.post('/password-reset/forgot-password', data),
  verifyResetToken: (data) => api.post('/password-reset/verify-reset-token', data),
  resetPassword: (data) => api.post('/password-reset/reset-password', data),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/activity'),
  getDashboardSummary: () => api.get('/dashboard/summary'),
  getDailyCapacity: () => api.get('/dashboard/capacity'),
};

// Moods API
export const moodsAPI = {
  logMood: (data) => api.post('/moods', data),
  getMoods: (params) => api.get('/moods/history', { params }),
  getStats: () => api.get('/moods/stats'),
  getTodayMoods: () => api.get('/moods/today'),
  clockIn: (data) => api.post('/moods/clock-in', data),
  clockOut: (data) => api.post('/moods/clock-out', data),
};

// Tasks API
export const tasksAPI = {
  getTasks: () => api.get('/tasks'),
  getTodayTasks: () => api.get('/tasks/today'),
  getOverdueTasks: () => api.get('/tasks/overdue'),
  getAllActiveTasks: () => api.get('/tasks/all-active'),
  createTask: (data) => api.post('/tasks', data),
  updateTask: (id, data) => api.put(`/tasks/${id}`, data),
  deleteTask: (id) => api.delete(`/tasks/${id}`),
  toggleComplete: (id) => api.patch(`/tasks/${id}/toggle`),
  completeTask: (id) => api.patch(`/tasks/${id}/complete`),
  pauseTask: (id) => api.patch(`/tasks/${id}/pause`),
  resumeTask: (id) => api.patch(`/tasks/${id}/resume`),
};

// Burnout API
export const burnoutAPI = {
  // Burnout Watch (new quiet protection system)
  getWatchStatus: () => api.get('/burnout/watch/status'),
  canAddTask: (weight) => api.post('/burnout/watch/can-add-task', { weight }),
  
  // Legacy endpoints (kept for backwards compatibility)
  getStatus: () => api.get('/burnout/status'),
  assess: (data) => api.post('/burnout/assess', data),
  getHistory: () => api.get('/burnout/history'),
  getRecommendations: () => api.get('/burnout/recommendations'),
  getLatest: () => api.get('/burnout/latest'),
  getRiskLevel: () => api.get('/burnout/risk'),
};

// Buddy API
export const buddyAPI = {
  // Matching
  findMatch: (data) => api.post('/buddy/match', data),
  getCurrentBuddy: () => api.get('/buddy/current'),
  deactivateMatch: (matchId) => api.post(`/buddy/match/${matchId}/deactivate`),
  
  // Interactions
  logInteraction: (data) => api.post('/buddy/interactions', data),
  getInteractions: (matchId) => api.get('/buddy/interactions', { params: { match_id: matchId } }),
  
  // Sessions
  bookSession: (data) => api.post('/buddy/sessions', data),
  getSessions: (status) => api.get('/buddy/sessions', { params: status ? { status } : {} }),
  updateSession: (id, data) => api.put(`/buddy/sessions/${id}`, data),
  cancelSession: (id) => api.delete(`/buddy/sessions/${id}`),
  getAvailability: () => api.get('/buddy/availability'),
  getQuota: () => api.get('/buddy/quota'),
};

// Recovery API
export const recoveryAPI = {
  getStatus: () => api.get('/recovery/status'),
  activate: (data) => api.post('/recovery/activate', data),
  deactivate: () => api.post('/recovery/deactivate'),
  update: (data) => api.put('/recovery/update', data),
  checkIn: (data) => api.post('/recovery/check-in', data),
  getCheckIns: (limit) => api.get('/recovery/check-ins', { params: limit ? { limit } : {} }),
  getResources: (params) => api.get('/recovery/resources', { params }),
  getResource: (id) => api.get(`/recovery/resources/${id}`),
  saveResource: (id) => api.post(`/recovery/resources/${id}/save`),
  getSavedResources: () => api.get('/recovery/saved'),
  startSession: (data) => api.post('/recovery/session', data),
  endSession: (id) => api.post(`/recovery/session/${id}/end`),
  getProgress: () => api.get('/recovery/progress'),
};

// Donations API
export const donationsAPI = {
  makeDonation: (data) => api.post('/donations', data),
  getHistory: () => api.get('/donations/history'),
  getStats: () => api.get('/donations/stats'),
  getCauses: () => api.get('/donations/causes'),
};

// Company API
export const companyAPI = {
  checkAccess: () => api.get('/company/check-access'),
  getCompanyStats: () => api.get('/company/dashboard'),
  getEmployees: () => api.get('/company/employees'),
  getWorkloadHeatmap: () => api.get('/company/workload-heatmap'),
  registerCompany: (data) => api.post('/company/register', data),
  inviteEmployee: (data) => api.post('/company/employees/invite', data),
  removeEmployee: (id) => api.delete(`/company/employees/${id}`),
  // Department management
  getDepartments: () => api.get('/company/departments'),
  createDepartment: (data) => api.post('/company/departments', data),
  updateDepartment: (id, data) => api.put(`/company/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/company/departments/${id}`),
  addDepartmentMember: (deptId, data) => api.post(`/company/departments/${deptId}/members`, data),
  removeDepartmentMember: (deptId, userId) => api.delete(`/company/departments/${deptId}/members/${userId}`),
  assignDepartmentTask: (deptId, data) => api.post(`/company/departments/${deptId}/tasks`, data),
  getDepartmentTasks: (deptId, params) => api.get(`/company/departments/${deptId}/tasks`, { params }),
  getDepartmentReport: (deptId, params) => api.get(`/company/departments/${deptId}/report`, { params }),
  getMyDepartments: () => api.get('/company/my-departments'),
  listEmployees: () => api.get('/company/employees/list'),
};

// Users API
export const usersAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  getSettings: () => api.get('/users/settings'),
  updateSettings: (data) => api.put('/users/settings', data),
};

export default api;
